import {
  defaultLoadableState,
  LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { CreditStatusResponse } from '~/services/home/contracts/FinanceData';
import { DeepPartial } from '~/types';

export type SptPriceDetailsContextType = {
  creditStatus: LoadableState<Partial<CreditStatusResponse>>;
  creditStatusRequest: number;
  isCoApplicantApplied: boolean;
  setCoApplicantApplied: React.Dispatch<React.SetStateAction<boolean>>;
  triggerCreditStatusRequest: () => void;
};

const defaultContext: SptPriceDetailsContextType = {
  creditStatus: defaultLoadableState<Partial<CreditStatusResponse>>({}),
  creditStatusRequest: 0,
  isCoApplicantApplied: false,
  setCoApplicantApplied() {},
  triggerCreditStatusRequest() {}
};

const SptPriceDetailsContext =
  createContext<SptPriceDetailsContextType>(defaultContext);

export function SptPriceDetailsContextProvider({
  children
}: React.PropsWithChildren) {
  const [isCoApplicantApplied, setCoApplicantApplied] = useState(
    defaultContext.isCoApplicantApplied
  );
  const creditStatus = useLoadableState<Partial<CreditStatusResponse>>({});
  const [creditStatusRequest, setCreditStatusRequest] = useState(
    defaultContext.creditStatusRequest
  );

  const triggerCreditStatusRequest = useCallback(() => {
    setCreditStatusRequest(Date.now());
  }, []);

  const value = useMemo<SptPriceDetailsContextType>(
    () => ({
      creditStatus,
      creditStatusRequest,
      isCoApplicantApplied,
      setCoApplicantApplied,
      triggerCreditStatusRequest
    }),
    [
      creditStatus,
      creditStatusRequest,
      isCoApplicantApplied,
      setCoApplicantApplied,
      triggerCreditStatusRequest
    ]
  );

  return (
    <SptPriceDetailsContext.Provider value={value}>
      {children}
    </SptPriceDetailsContext.Provider>
  );
}

SptPriceDetailsContext.displayName = 'SptPriceDetailsContextProvider';

export default SptPriceDetailsContext;

export const generateSptPriceDetailsContext = (
  customState: DeepPartial<SptPriceDetailsContextType>
): SptPriceDetailsContextType =>
  deepmerge<
    SptPriceDetailsContextType,
    DeepPartial<SptPriceDetailsContextType>
  >(defaultContext, customState);

export function useCreditStatus() {
  return useContext(SptPriceDetailsContext).creditStatus.data;
}
